import React, { useEffect, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { isEmpty } from "lodash";
import { makeStyles, withStyles } from "tss-react/mui";

import client from "@/client";
import FormPaperIcon from "@/components/Icons/FormPaperIcon";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { OrderByDirection, SentFormStatusForCoach } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { parseDateTime } from "@/utils/datetime";
import { humanize } from "@/utils/string";

import SendReminderDialog from "./components/SendReminderDialog";
import SentFormHeader from "./components/SentFormHeader";
import SentFormOptionMenu from "./components/SentFormOptionMenu";
import UnsendFormModal from "./components/UnsendFormModal";

const Root = styled("div")({
  height: "100vh",
  padding: 1,
  backgroundColor: colors.white,
});

const useStyles = makeStyles()({
  title: {
    fontSize: 20,
    fontWeight: 700,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
  },
  table: {
    minWidth: 650,
  },
  actionButtons: {
    display: "flex",
    gap: 10,
  },
  emptyContentWrapper: { margin: "auto" },
  emptyLabel: {
    color: "#AAB0BB",
    fontWeight: 700,
    display: "flex",
    justifyContent: "center",
  },
  emptyIcon: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20%",
  },
  headerFilter: {
    minWidth: 180,
  },
  cancelButton: {
    color: "#C8CED8",
    borderColor: "#C8CED8",
  },
  action: {
    "&.MuiButton-root": {
      fontSize: 12,
      fontWeight: 700,
      borderStyle: "solid",
      borderWidth: 2,
      borderColor: "#D4DBE5",
      color: colors.primary,
    },
  },
  completedBy: {
    marginLeft: 4,
    width: 16,
    height: 16,
  },
});

const StyledTableContainer = withStyles(TableContainer, {
  root: {
    paddingBottom: 20,
  },
});

const StyledTableHead = withStyles(TableHead, {
  root: {
    backgroundColor: "#EDF0F5",
  },
});

const THeadTypography = withStyles(Typography, {
  root: {
    fontWeight: 600,
    color: colors.grey,
  },
});

const TBodyTypography = withStyles(Typography, {
  root: {
    fontWeight: 600,
    color: colors.dark,
  },
});

const StyledActionButton = withStyles(Button, {
  root: {
    fontSize: 12,
    fontWeight: 700,
    borderStyle: "solid",
    borderWidth: 2,
    borderColor: "#D4DBE5",
    color: colors.primary,
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
});

const StyledTableBody = withStyles(TableBody, {});

const StyledTableCell = withStyles(TableCell, {
  root: {
    borderRight: "1px solid #DEE6F0",
    backgroundColor: "transparent",
  },
});

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiInputBase-input": {
      display: "flex",
      alignItems: "center",
      height: 52,
      padding: 0,
      paddingLeft: 16,
      paddingRight: 28,
    },
  },
});

const StatusColors = {
  [SentFormStatusForCoach.NoResponse]: "#FF7878",
  [SentFormStatusForCoach.Completed]: colors.dark,
  [SentFormStatusForCoach.Unsent]: colors.secondary,
};

export const sentFormsQuery = graphql(`
  query SentForms(
    $orderByTitle: OrderByDirection
    $orderByRecipient: OrderByDirection
    $orderByDate: OrderByDirection
  ) {
    sentForms(
      orderByTitle: $orderByTitle
      orderByRecipient: $orderByRecipient
      orderByDate: $orderByDate
    ) {
      nodes {
        id
        client {
          id
          profile {
            id
            fullName
            firstName
          }
        }
        form {
          id
          title
        }
        createdAt
        statusForCoach
        openUrlForCoach
        answerUrlForCoach
        completedBy {
          id
          profile {
            id
            fullName
            avatarUrl(variant: THUMB)
          }
        }
      }
    }
  }
`);

const unsendFormMutation = graphql(`
  mutation UnsendForm($input: UnsendFormInput!) {
    unsendForm(input: $input) {
      sentForm {
        id
      }
    }
  }
`);

const SentForms: React.FC = () => {
  const [filterValue, setFilterValue] =
    useState<"ALL" | SentFormStatusForCoach>("ALL");
  const sendReminder = useDialogState();
  const { classes } = useStyles();
  const [sortByTitle, setSortByTitle] = useState<OrderByDirection | null>(null);
  const [sortByRecipient, setSortByRecipient] =
    useState<OrderByDirection | null>(null);
  const [sortByDate, setSortByDate] = useState<OrderByDirection | null>(null);

  const { data, loading } = useQuery(sentFormsQuery);
  const [items, setItems] = useState(data?.sentForms.nodes ?? []);

  useEffect(() => {
    const filteredItems =
      data?.sentForms.nodes.filter(
        (form) => filterValue == "ALL" || form.statusForCoach === filterValue
      ) ?? [];
    setItems(filteredItems);
  }, [filterValue]);

  const [unsendForm] = useMutation(unsendFormMutation);
  const [unsendFormVisible, setunsendFormVisible] = useState<boolean>(false);
  const [currentSentFormId, setCurrentSentFormId] = useState<string>("");
  const snackbar = useGlobalSnackbar();

  function handleStatusFilter(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setFilterValue(value as "ALL" | SentFormStatusForCoach);
  }

  async function handleQueryForItems(
    sort: OrderByDirection | null,
    type: string
  ) {
    let queryInput = {};
    switch (type) {
      case "title":
        queryInput = { orderByTitle: sort };
        break;
      case "recipient":
        queryInput = { orderByRecipient: sort };
        break;
      case "date":
        queryInput = { orderByDate: sort };
        break;
      default:
        break;
    }

    const result = await client.query({
      query: sentFormsQuery,
      variables: queryInput,
    });
    const filteredItems =
      result.data.sentForms.nodes.filter(
        (form) => filterValue == "ALL" || form.statusForCoach === filterValue
      ) ?? [];
    setItems(filteredItems ?? []);
  }

  function parseSort(sort: OrderByDirection | null): OrderByDirection | null {
    if (sort == null) {
      return OrderByDirection.Asc;
    }

    if (sort == OrderByDirection.Asc) {
      return OrderByDirection.Desc;
    } else {
      return null;
    }
  }

  function handleSort(type: string, val: OrderByDirection | null) {
    let sort;
    switch (type) {
      case "title":
        sort = parseSort(val);
        handleQueryForItems(sort, "title");
        setSortByTitle(sort);
        break;
      case "recipient":
        sort = parseSort(val);
        handleQueryForItems(sort, "recipient");
        setSortByRecipient(sort);
        break;
      case "date":
        sort = parseSort(val);
        handleQueryForItems(sort, "date");
        setSortByDate(sort);
        break;

      default:
        break;
    }
  }

  async function handleUnsendForm() {
    try {
      await unsendForm({
        variables: { input: { id: currentSentFormId } },
        refetchQueries: [sentFormsQuery],
      });
      setunsendFormVisible(false);
      snackbar.open("Form unsent successfully.");
    } catch (error) {
      snackbar.open(`There was an error in unsending your form. ${error}`);
    }
  }

  function handleSendReminder(id: string) {
    setCurrentSentFormId(id);
    sendReminder.show();
  }

  function handleUnsendFormOpen(id: string) {
    setunsendFormVisible(true);
    setCurrentSentFormId(id);
  }

  if (loading && items == null) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        padding={14}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Root>
        {isEmpty(data?.sentForms.nodes) ? (
          <Box className={classes.emptyContentWrapper}>
            <Box className={classes.emptyIcon}>
              <FormPaperIcon />
            </Box>
            <Typography className={classes.emptyLabel}>No Forms yet</Typography>
          </Box>
        ) : (
          <>
            <Box className={classes.header}>
              <Typography className={classes.title}>Sent Forms</Typography>
              <StyledTextField
                select
                className={classes.headerFilter}
                value={filterValue}
                onChange={handleStatusFilter}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value={SentFormStatusForCoach.Completed}>
                  Completed
                </MenuItem>
                <MenuItem value={SentFormStatusForCoach.NoResponse}>
                  No Response
                </MenuItem>
                <MenuItem value={SentFormStatusForCoach.Unsent}>
                  Unsent
                </MenuItem>
              </StyledTextField>
            </Box>
            <StyledTableContainer>
              <Table className={classes.table} stickyHeader>
                <StyledTableHead>
                  <TableRow>
                    <StyledTableCell>
                      <SentFormHeader
                        handleSort={handleSort}
                        sortValue={sortByTitle}
                        title="Form Title"
                        type="title"
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <SentFormHeader
                        handleSort={handleSort}
                        sortValue={sortByRecipient}
                        title="Sent to"
                        type="recipient"
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <SentFormHeader
                        handleSort={handleSort}
                        sortValue={sortByDate}
                        title="Date Sent"
                        type="date"
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <THeadTypography variant="body2">
                        Progress
                      </THeadTypography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <THeadTypography variant="body2">Action</THeadTypography>
                    </StyledTableCell>
                  </TableRow>
                </StyledTableHead>
                <StyledTableBody>
                  {items.map((data, index) => (
                    <TableRow key={index}>
                      <StyledTableCell>
                        <TBodyTypography variant="body1">
                          {data.form.title}
                        </TBodyTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <TBodyTypography variant="body1">
                          {data.client?.profile?.fullName}
                        </TBodyTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <TBodyTypography variant="body1">
                          {format(
                            parseDateTime(data.createdAt),
                            "dd MMM, yyyy | hh:mm aa"
                          )}
                        </TBodyTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box display="flex" alignItems="center">
                          <TBodyTypography
                            style={{
                              color: StatusColors[data.statusForCoach],
                            }}
                          >
                            {humanize(data.statusForCoach)}
                          </TBodyTypography>
                          {data.statusForCoach ===
                            SentFormStatusForCoach.Completed && (
                            <Avatar
                              className={classes.completedBy}
                              alt={data.completedBy?.profile?.fullName}
                              src={
                                data.completedBy?.profile?.avatarUrl ??
                                undefined
                              }
                            />
                          )}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box className={classes.actionButtons}>
                          {data.statusForCoach ===
                            SentFormStatusForCoach.NoResponse && (
                            <>
                              <Button
                                className={classes.action}
                                variant="outlined"
                                component="a"
                                href={data.answerUrlForCoach}
                                startIcon={<LaunchIcon fontSize="small" />}
                                target="_blank"
                              >
                                Open
                              </Button>
                              {Features.formsSendReminder && (
                                <StyledActionButton onClick={sendReminder.show}>
                                  Send Reminder
                                </StyledActionButton>
                              )}
                              {Features.formsEditSentForm && (
                                <StyledActionButton>Edit</StyledActionButton>
                              )}
                            </>
                          )}
                          {data.statusForCoach ===
                            SentFormStatusForCoach.Completed && (
                            <>
                              <Button
                                className={classes.action}
                                variant="outlined"
                                component="a"
                                href={data.openUrlForCoach}
                                startIcon={<LaunchIcon fontSize="small" />}
                                target="_blank"
                              >
                                Open
                              </Button>
                              {Features.formsEditSentForm && (
                                <StyledActionButton>Edit</StyledActionButton>
                              )}
                            </>
                          )}
                          {data.statusForCoach ==
                            SentFormStatusForCoach.NoResponse && (
                            <SentFormOptionMenu
                              onSendReminder={handleSendReminder}
                              handleUnsendFormOpen={handleUnsendFormOpen}
                              id={data.id}
                            />
                          )}
                        </Box>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </StyledTableBody>
              </Table>
            </StyledTableContainer>
          </>
        )}
        <UnsendFormModal
          onClose={() => setunsendFormVisible(false)}
          visibility={unsendFormVisible}
          handleUnsend={handleUnsendForm}
        />
      </Root>
      <SendReminderDialog
        onClose={sendReminder.hide}
        open={sendReminder.shown}
        id={currentSentFormId}
      />
    </>
  );
};

export default SentForms;
